import { useAlerts } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import {
  selectCommentary,
  selectCommentaryV2,
} from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOpinionOfSiteValuePrice } from '../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import { selectCompSequence, selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsEntities } from '../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationById } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { OrganizedValidationErrors, ValidationErrorType, ValidationKey } from './types.ts';
import { schAppraisifyValidation } from './validationSchema.ts';

export const useAppraisifyValidation = () => {
  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const order = useAppSelector(selectOrder);
  const compSequence = useAppSelector(selectCompSequence);
  const improvements = useAppSelector(selectImprovementsEntities);
  const opinionOfSiteValue = useAppSelector(selectOpinionOfSiteValuePrice);
  const { marketConditionsV2, priorSales } = useAppSelector(selectCommentaryV2);
  const { totalCostToCure, opinionOfMarketValue, includeTotalCostToCure } =
    useAppSelector(selectReconciliation);
  const { reconciliation, alerts: alertsCommentary } = useAppSelector(selectCommentary);

  const alerts = useAlerts();
  const allAlerts = Object.values(alerts).flat();

  const validationForm = schAppraisifyValidation.safeParse({
    alerts: {
      alerts: allAlerts,
    },
    orderDetails: {
      zoning: subjectPropertyIdentification?.zoning ?? '',
      zoningCompliance: subjectPropertyIdentification?.zoningCompliance ?? '',
      parcelNumber: subjectPropertyIdentification?.parcelNumber ?? '',
      legalDescription: subjectPropertyIdentification?.legalDescription ?? '',
      effectiveDate: order.effectiveDate ?? '',
      intendedUsers: order.intendedUsers ?? '',
      intendedUse: order.intendedUse ?? '',
      propertyInterest: subjectPropertyIdentification?.propertyInterest ?? '',
      propertyInterestOther: subjectPropertyIdentification?.propertyInterestOther ?? '',
    },
    analysis: {
      comps: compSequence ?? [],
      opinionOfSiteValue: opinionOfSiteValue ?? '',
    },
    commentary: {
      estimatedDemand: marketConditionsV2?.estimatedDemandSupply ?? '',
      estimatedExposureTime: marketConditionsV2?.estimatedExposureTime ?? '',
      marketConditionCommentary: marketConditionsV2?.commentary ?? '',
      priorSalesCommentary: priorSales?.commentary ?? '',
    },
    reconciliation: {
      totalCostToCure: totalCostToCure ?? '',
      opinionOfMarketValue: opinionOfMarketValue ?? '',
      reconciliationCommentary: reconciliation?.reconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliation?.highestAndBestUseCommentary ?? '',
      revisionCommentary: reconciliation?.revisionCommentary ?? '',
      alternativeComparablesCommentary: reconciliation?.alternativeComparablesCommentary ?? [],
    },
  });

  // Process validation errors from Zod schema
  const processValidationErrors = (): OrganizedValidationErrors => {
    const organizedErrors: OrganizedValidationErrors = {
      Alerts: {},
      OrderDetails: {},
      Analysis: {},
      Market: {},
      Commentary: {},
      Reconciliation: {},
    };

    if (!order?.isInspectifyOrder) {
      return organizedErrors;
    }

    // Add a validation error if there are dismissed alerts without commentary
    const dismissedAlerts = allAlerts.filter(({ status }) => status === 'dismissed');
    if (dismissedAlerts.length > 0) {
      const hasDismissedAlertsWithoutCommentary = dismissedAlerts.some(
        (alert) =>
          alertsCommentary?.[alert.type] === undefined ||
          alertsCommentary?.[alert.type]?.trim() === '',
      );

      if (hasDismissedAlertsWithoutCommentary) {
        organizedErrors.Alerts[ValidationKey.AlertsCommentary] = {
          type: ValidationErrorType.REQUIRED,
          message: 'Please provide commentary for all dismissed alerts.',
        };
      }
    }

    // Check if cost quality is defined for subject and all comparables
    const subjectQuality = improvements?.subject?.craftsmanQuality;
    const hasMissingQuality =
      !subjectQuality ||
      (compSequence && compSequence.some((compId) => !improvements?.[compId]?.craftsmanQuality));

    if (hasMissingQuality) {
      organizedErrors.Analysis.costQuality = {
        type: ValidationErrorType.REQUIRED,
        message: 'Required',
      };
    }

    if (validationForm.error) {
      // Process Zod validation errors
      validationForm.error.issues.forEach((issue) => {
        const { path, code } = issue;
        let { message } = issue;

        if (path[path.length - 1] === 'revisionCommentary' && !order.isRevision) {
          return;
        }

        if (
          path[path.length - 1] === 'alternativeComparablesCommentary' &&
          (!order.isRevision || !order.revisionData?.comparables)
        ) {
          return;
        }

        if (path[path.length - 1] === 'totalCostToCure' && !includeTotalCostToCure) {
          return;
        }

        // Map field paths to validation keys and modules
        if (path.length >= 2) {
          const module = path[0] as string;
          const field = path[1] as string;

          let validationKey: ValidationKey | null = null;
          let targetModule: keyof OrganizedValidationErrors | null = null;

          // Map module and field to validation key and target module˝
          switch (module) {
            case 'alerts':
              targetModule = 'Alerts';
              switch (field) {
                case 'alerts':
                  validationKey = ValidationKey.Alerts;
                  break;
                default:
                  break;
              }
              break;
            case 'orderDetails':
              targetModule = 'OrderDetails';
              switch (field) {
                case 'zoning':
                  validationKey = ValidationKey.Zoning;
                  break;
                case 'zoningCompliance':
                  validationKey = ValidationKey.ZoningCompliance;
                  if (code === 'invalid_enum_value' && issue.received === '') {
                    message = 'Please select a zoning compliance option.';
                  }
                  break;
                case 'parcelNumber':
                  validationKey = ValidationKey.ParcelNumber;
                  break;
                case 'legalDescription':
                  validationKey = ValidationKey.LegalDescription;
                  break;
                case 'effectiveDate':
                  validationKey = ValidationKey.EffectiveDate;
                  break;
                case 'intendedUsers':
                  validationKey = ValidationKey.IntendedUsers;
                  break;
                case 'intendedUse':
                  validationKey = ValidationKey.IntendedUse;
                  break;
                case 'propertyInterest':
                  validationKey = ValidationKey.PropertyInterest;
                  if (code === 'invalid_enum_value' && issue.received === '') {
                    message = 'Please select a property interest option.';
                  }
                  break;
                case 'propertyInterestOther':
                  validationKey = ValidationKey.PropertyInterestOther;
                  break;
                default:
                  break;
              }
              break;
            case 'analysis':
              targetModule = 'Analysis';
              switch (field) {
                case 'comps':
                  validationKey = ValidationKey.Comps;
                  message = 'At least one comparable is required';
                  break;
                case 'opinionOfSiteValue':
                  validationKey = ValidationKey.OpinionOfSiteValue;
                  if (code === 'invalid_union')
                    message = 'Please enter a valid site value before continuing.';
                  break;
                default:
                  break;
              }
              break;
            case 'market':
              targetModule = 'Market';
              switch (field) {
                default:
                  break;
              }
              break;
            case 'commentary':
              targetModule = 'Commentary';
              switch (field) {
                case 'estimatedDemand':
                  validationKey = ValidationKey.EstimatedDemand;
                  break;
                case 'estimatedExposureTime':
                  validationKey = ValidationKey.EstimatedExposureTime;
                  break;
                case 'marketConditionCommentary':
                  validationKey = ValidationKey.MarketConditionCommentary;
                  break;
                case 'priorSalesCommentary':
                  validationKey = ValidationKey.PriorSalesCommentary;
                  break;
                default:
                  break;
              }
              break;
            case 'reconciliation':
              targetModule = 'Reconciliation';
              switch (field) {
                case 'totalCostToCure':
                  validationKey = ValidationKey.TotalCostToCure;
                  message = 'Required';

                  break;
                case 'opinionOfMarketValue':
                  validationKey = ValidationKey.OpinionOfMarketValue;
                  message = 'Required';

                  break;
                case 'reconciliationCommentary':
                  validationKey = ValidationKey.ReconciliationCommentary;
                  break;
                case 'highestAndBestUseCommentary':
                  validationKey = ValidationKey.HighestAndBestUseCommentary;
                  break;
                case 'revisionCommentary':
                  validationKey = ValidationKey.RevisionCommentary;
                  break;
                case 'alternativeComparablesCommentary':
                  validationKey = ValidationKey.AlternativeComparablesCommentary;
                  break;
                default:
                  break;
              }
              break;
            default:
              break;
          }

          // Add error to organized structure
          if (validationKey && targetModule) {
            organizedErrors[targetModule][validationKey] = {
              type: ValidationErrorType.REQUIRED,
              message,
            };
          }
        }
      });
    }

    return organizedErrors;
  };

  const errors = processValidationErrors();

  return {
    errors,
  };
};
